/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Post-authentication bootstrap screen. Same chrome as the auth flow
// (wallpaper + logomark + Lato title) so the transition from sign-in
// → home feels continuous. Cycles through human-readable progress
// steps so the user knows the app is doing real work.

import { useEffect, useState } from 'react';
import { ActivityIndicator, Text, View, useColorScheme } from 'react-native';
import { PingLogomark } from '../icons';
import { tokens } from '../design-system/tokens';
import { AuthWallpaper } from './AuthWallpaper';

export interface BootstrappingScreenProps {
	readonly steps?: readonly string[];
	readonly stepDurationMs?: number;
	readonly title?: string;
}

const DEFAULT_STEPS: readonly string[] = [
	'Securing your connection…',
	'Loading your conversations…',
	'Setting up encryption…',
	'Almost ready…',
];

export function BootstrappingScreen(props: BootstrappingScreenProps): JSX.Element {
	const steps = props.steps && props.steps.length > 0 ? props.steps : DEFAULT_STEPS;
	const stepDurationMs = props.stepDurationMs ?? 2400;
	const title = props.title ?? 'Preparing your account';
	const [stepIndex, setStepIndex] = useState(0);
	const scheme = useColorScheme();
	const spinnerColor = tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'].accent.brand;

	useEffect(() => {
		if (steps.length <= 1) return;
		const id = setInterval(() => {
			setStepIndex((curr) => (curr + 1) % steps.length);
		}, stepDurationMs);
		return () => clearInterval(id);
	}, [steps, stepDurationMs]);

	const activeStep = steps[stepIndex] ?? steps[0];

	return (
		<View
			testID="app-bootstrapping"
			accessibilityLabel="bootstrapping"
			className="relative w-full min-h-screen items-center justify-center overflow-hidden bg-light-surface-app dark:bg-dark-surface-app"
		>
			<AuthWallpaper />
			<View className="relative items-center justify-center gap-xl px-xl">
				<PingLogomark size={80} />
				<Text className="font-sans font-bold text-[20px] leading-[28px] text-light-text-primary dark:text-dark-text-primary text-center">
					{title}
				</Text>
				<View className="flex-row items-center gap-md mt-xs">
					<ActivityIndicator size="small" color={spinnerColor} />
					<Text
						accessibilityLiveRegion="polite"
						className="font-sans font-normal text-[16px] leading-[22px] text-light-text-secondary dark:text-dark-text-secondary text-center"
					>
						{activeStep}
					</Text>
				</View>
			</View>
		</View>
	);
}
